import { key, keyOf, parseKey, type Coord, type CoordKey, type Run, type Tile } from './types.js';
import { faceOf } from './tiles.js';

export type Grid = Map<CoordKey, Tile>;

/**
 * Extract every maximal run of two or more orthogonally adjacent tiles.
 *
 * "Maximal" is the whole rule in one word. A run is grown until it hits a gap,
 * so CAR written immediately before MUCH on the same row is not two runs — it
 * is the single run CARMUCH, which then has to answer to the dictionary like
 * any other word. Leaving a blank square between them is what makes them two
 * separate words, exactly as on a physical table.
 */
export function extractRuns(grid: Grid): Run[] {
  const runs: Run[] = [];
  if (grid.size === 0) return runs;

  for (const orientation of ['across', 'down'] as const) {
    const dx = orientation === 'across' ? 1 : 0;
    const dy = orientation === 'across' ? 0 : 1;
    for (const k of grid.keys()) {
      const { x, y } = parseKey(k);
      // Only start a run at its head: the cell before it must be empty.
      if (grid.has(key(x - dx, y - dy))) continue;
      const cells: Coord[] = [];
      let word = '';
      let cx = x;
      let cy = y;
      while (true) {
        const tile = grid.get(key(cx, cy));
        if (!tile) break;
        cells.push({ x: cx, y: cy });
        const face = faceOf(tile);
        word += face ?? '?';
        cx += dx;
        cy += dy;
      }
      if (cells.length >= 2) {
        runs.push({ orientation, start: { x, y }, cells, word });
      }
    }
  }
  return runs;
}

/** Cells that belong to no run of length >= 2 — orphans in a crossword sense. */
export function loneCells(grid: Grid): Coord[] {
  const covered = new Set<CoordKey>();
  for (const run of extractRuns(grid)) {
    for (const c of run.cells) covered.add(keyOf(c));
  }
  const lone: Coord[] = [];
  for (const k of grid.keys()) {
    if (!covered.has(k)) lone.push(parseKey(k));
  }
  return lone;
}

/**
 * Split the grid into orthogonally connected components, largest first.
 * A finished layout must be exactly one component — the words all have to hang
 * together through shared letters.
 */
export function components(grid: Grid): CoordKey[][] {
  const seen = new Set<CoordKey>();
  const out: CoordKey[][] = [];
  for (const start of grid.keys()) {
    if (seen.has(start)) continue;
    const comp: CoordKey[] = [];
    const stack = [start];
    seen.add(start);
    while (stack.length) {
      const k = stack.pop() as CoordKey;
      comp.push(k);
      const { x, y } = parseKey(k);
      const neighbours = [key(x + 1, y), key(x - 1, y), key(x, y + 1), key(x, y - 1)];
      for (const n of neighbours) {
        if (grid.has(n) && !seen.has(n)) {
          seen.add(n);
          stack.push(n);
        }
      }
    }
    out.push(comp);
  }
  out.sort((a, b) => b.length - a.length);
  return out;
}

export interface Bounds { minX: number; minY: number; maxX: number; maxY: number; }

/** Tight bounding box of the occupied cells, or null for an empty grid. */
export function bounds(grid: Grid): Bounds | null {
  if (grid.size === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const k of grid.keys()) {
    const { x, y } = parseKey(k);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Can `moves` (tileId -> destination) be applied without collision?
 *
 * Cells vacated by the moving tiles are treated as free, which is what makes
 * sliding a whole selected block by one square work rather than tripping over
 * its own tail.
 */
export function canPlaceAll(
  grid: Grid,
  moves: ReadonlyMap<string, Coord>,
): { ok: true } | { ok: false; conflicts: Coord[] } {
  const movingIds = new Set(moves.keys());
  const vacated = new Set<CoordKey>();
  for (const [k, tile] of grid) {
    if (movingIds.has(tile.id)) vacated.add(k);
  }
  const taken = new Set<CoordKey>();
  const conflicts: Coord[] = [];
  for (const dest of moves.values()) {
    const dk = keyOf(dest);
    const occupied = grid.has(dk) && !vacated.has(dk);
    if (occupied || taken.has(dk)) conflicts.push(dest);
    taken.add(dk);
  }
  return conflicts.length ? { ok: false, conflicts } : { ok: true };
}

/** Apply a validated batch of moves, returning a new grid. */
export function applyMoves(grid: Grid, moves: ReadonlyMap<string, Coord>): Grid {
  const next: Grid = new Map();
  const movingIds = new Set(moves.keys());
  for (const [k, tile] of grid) {
    if (!movingIds.has(tile.id)) next.set(k, tile);
  }
  const byId = new Map<string, Tile>();
  for (const tile of grid.values()) byId.set(tile.id, tile);
  for (const [id, dest] of moves) {
    const tile = byId.get(id);
    if (tile) next.set(keyOf(dest), tile);
  }
  return next;
}

/** Cells inside an inclusive rectangle — the marquee-selection primitive. */
export function cellsInRect(grid: Grid, a: Coord, b: Coord): Coord[] {
  const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
  const out: Coord[] = [];
  for (const k of grid.keys()) {
    const c = parseKey(k);
    if (c.x >= minX && c.x <= maxX && c.y >= minY && c.y <= maxY) out.push(c);
  }
  return out;
}

/**
 * The connected block of tiles reachable from `origin` — what a long-press
 * "grab this word and its neighbours" gesture selects.
 */
export function connectedBlock(grid: Grid, origin: Coord): Coord[] {
  const startKey = keyOf(origin);
  if (!grid.has(startKey)) return [];
  const comp = components(grid).find((c) => c.includes(startKey));
  return (comp ?? []).map(parseKey);
}

/** Every cell of the runs passing through `origin` (across and down). */
export function runsThrough(grid: Grid, origin: Coord): Run[] {
  const ok = keyOf(origin);
  return extractRuns(grid).filter((r) => r.cells.some((c) => keyOf(c) === ok));
}
